import {checkForGlError} from "#webgl2/helpers/gl-error"
import {createDynamicBuffer} from "#webgl2/webgl2-shader-utils"

export class WebGl2GeometryIndexBuffer {
    constructor(
        private gl: WebGL2RenderingContext,
        initialCapacity = 64,
    ) {
        this.allocate(initialCapacity)
    }

    dispose(): void {
        if (this._buffer) {
            this.gl.deleteBuffer(this._buffer)
            this._buffer = undefined
        }
        this._data = undefined
    }

    get size(): number {
        return this._numElements
    }

    get data(): Readonly<Uint32Array> {
        if (!this._data) {
            throw Error("WebGl2GeometryIndexBuffer: No data allocated")
        }
        return this._data.subarray(0, this._numElements)
    }

    clear() {
        this._numElements = 0
    }

    add(data: number[] | Uint32Array) {
        const numElements = data.length
        if (numElements === 0) {
            return
        }
        this.reserve(this._numElements + numElements)
        if (!this._data) {
            throw Error("WebGl2GeometryIndexBuffer: No data allocated")
        }
        this._data.set(data, this._numElements)
        this._numElements += numElements
        this._needsUpload = true
    }

    remove(startIndex: number, count?: number): void {
        count ??= this._numElements - startIndex
        if (startIndex < 0 || startIndex + count > this._numElements) {
            throw Error("Invalid index range")
        }
        if (!this._data) {
            throw Error("WebGl2GeometryIndexBuffer: No data allocated")
        }
        this._data.set(this._data.subarray(startIndex + count, this._numElements), startIndex)
        this._numElements -= count
        this._needsUpload = true
    }

    bind(): void {
        if (!this._buffer) {
            throw Error("WebGl2GeometryIndexBuffer: No buffer allocated")
        }
        this.updateBuffer()
        this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, this._buffer)
        checkForGlError(this.gl)
    }

    private updateBuffer() {
        if (!this._needsUpload) {
            return
        }
        if (!this._data || !this._buffer) {
            throw Error("WebGl2GeometryIndexBuffer: No data or buffer allocated")
        }
        this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, this._buffer)
        this.gl.bufferSubData(this.gl.ELEMENT_ARRAY_BUFFER, 0, this._data)
        checkForGlError(this.gl)
        this._needsUpload = false
    }

    private reserve(numElements: number): void {
        if (numElements <= this._capacity) {
            return
        }
        const newCapacity = Math.max(this._capacity * 2, numElements)
        this.allocate(newCapacity)
        this._needsUpload = true
    }

    private allocate(newCapacity: number): void {
        if (newCapacity <= 0) {
            throw Error("WebGl2GeometryIndexBuffer: Capacity must be greater than 0")
        }
        const prevData = this._data
        this._capacity = newCapacity
        this._data = new Uint32Array(this._capacity)
        if (prevData) {
            this._data.set(prevData)
        }
        if (this._buffer) {
            this.gl.deleteBuffer(this._buffer)
        }
        this._buffer = createDynamicBuffer(this.gl, this.gl.ELEMENT_ARRAY_BUFFER, this._capacity * 4) // 32bit int
    }

    private _capacity = 0
    private _numElements = 0
    private _data?: Uint32Array
    private _buffer?: WebGLBuffer
    private _needsUpload = false
}
